import { defineStore } from "pinia";
import { useAxios } from "@/composables/api.js";
import { useCandidateStore } from "@/stores/candidate.js";
import { useSpeedTestStore } from "./speedtest";
import { ref } from "vue";
import mitt from "mitt";

export const useCloudClientStore = defineStore("cloud-client", () => {
  const emitter = mitt();

  const candidateStore = useCandidateStore();
  const speedtest = useSpeedTestStore();

  let participant_id = null;
  let campaign_creation_date = null;
  let candidate_id = null;
  let session_id = null;
  let mode = null;
  let yuzu_version = 1;
  let socket = null;
  let pixelStreamingURL = ref(null);
  let speedtestURL = null;
  let pingInterval = null;

  function init(new_participant_id, cmp_version, campaign_creation_date_in, campaign_mode) {
    participant_id = new_participant_id;
    candidate_id = candidateStore.candidate.candidate_id;
    yuzu_version = cmp_version;
    pixelStreamingURL.value = null;
    campaign_creation_date = campaign_creation_date_in;
    mode = campaign_mode;
  }

  function canStart() {
    return participant_id != null && candidate_id != null;
  }

  async function startSession() {
    try {
      const { data } = await useAxios.post(
        `/candidates/me/assessments/${participant_id}`
      );

      session_id = data.session_id;

      return true;
    } catch (error) {
      return false;
    }
  }

  async function connectToCloudOperator() {
    if (!canStart() || session_id == null) {
      return false;
    }

    let serverURL = import.meta.env.VITE_CLOUD_OPERATOR_URL;

    //console.log("connecting to cloud operator from URL '" + serverURL + "' ...");

    socket = new WebSocket(serverURL);

    socket.addEventListener("open", (e) => {
      onOpen.call(this, e);
    });
    socket.addEventListener("message", (e) => {
      onMessage.call(this, e);
    });
    socket.addEventListener("close", onClose);
    socket.addEventListener("error", onError);

    return true;
  }

  async function disconnectFromCloudOperator() {
    return new Promise((resolve, reject) => {
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        // Already closed, resolve immediately
        resolve();
        return;
      }

      // Listen for the close event to resolve the promise
      socket.onclose = () => {
        resolve();
      };

      // Handle any potential errors
      socket.onerror = (error) => {
        reject(error);
      };

      // Close the WebSocket
      socket.close();
    });
  }

  async function tryStartSession() {
    let ip;
    try {
      const res = await useAxios.get("/getIP");
      ip = res.data.ip;
    } catch (e) {
      ip = "127.0.0.1";
    }
    sendMessage("/candidates/can-create-cloud-session", {
      candidate_id: candidate_id,
      session_id: session_id,
      version: yuzu_version,
      ip,
      campaign_creation_date: campaign_creation_date,
    });
  }

  function startCloudSession() {
    if (!canStart() || session_id == null) {
      return false;
    }

    sendMessage("/candidates/create-cloud-session", {
      session_id: session_id,
      candidate_id: candidate_id,
      participant_id: participant_id,
      candidate_email: candidateStore.candidate.email,
      candidate_first_name: candidateStore.candidate.first_name,
      candidate_last_name: candidateStore.candidate.last_name,
      version: yuzu_version,
      campaign_creation_date: campaign_creation_date,
      mode: mode,
    });

    return true;
  }

  function onOpen() {
    pingInterval = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        sendMessage("/ping", {});
      }
    }, 1000);
    tryStartSession();
  }

  function onMessage(event) {
    let websocketData = JSON.parse(event.data);

    if (websocketData.route == "/candidates/cloud-session-status") {
      onCloudStatusReceived.call(this, websocketData.data);
    } else if (websocketData.route == "/candidates/cloud-session-created") {
      onCloudSessionStarted();
    } else if (websocketData.route == "/candidates/cloud-session-available") {
      onPixelStreamingReady.call(this, websocketData.data);
    } else if (websocketData.route == "/candidates/session-quality") {
      onSessionQuality.call(this, websocketData.data);
    } else if (websocketData.route == "/error-events") {
      onErrorEvent.call(this, websocketData.data);
    } else if (websocketData.route == "/candidates/restart-tests") {
      onErrorEvent.call(this, "InsufficientCapacity");
    }
  }

  function onClose(event) {
    console.log("websocket closed", event);
    emitter.emit("onCloudOperatorSocketDisconnected");
    clearInterval(pingInterval);
    pingInterval = null;
  }

  function onError(error) {
    console.log("websocket error", error);
    emitter.emit("onCloudOperatorSocketError");
  }

  function sendMessage(route, data) {
    let obj = {
      route: route,
      data: data,
    };
    socket.send(JSON.stringify(obj));

    //console.log("message sent", obj);
  }

  function onCloudStatusReceived(data) {
    if (data.speedtest_url) {
      speedtestURL = data.speedtest_url;
      emitter.emit("onSpeedTestURLAvailable");
    }
    if (data.available) {
      emitter.emit("onSessionAvailable");
    } else {
      emitter.emit("onSessionNotAvailable", data);
      emitter.emit("onCancelSpeedtest", data);
    }
  }

  function onCloudSessionStarted() {}

  function onPixelStreamingReady(data) {
    pixelStreamingURL.value = "http://" + data.pixel_streaming_url;
    console.log("pixel streaming ready on " + pixelStreamingURL.value);
  }

  function onSessionQuality(data) {
    console.log("Quality:", data);
    emitter.emit("onCloudOperatorSessionQuality", data.data);
  }

  function onErrorEvent(data) {
    if (data.error_code === "InsufficientCapacity") {
      emitter.emit("onCapacityError");
    }
  }

  function startPingTest() {
    let pingCount = parseInt(import.meta.VITE_SPEEDTEST_PING_COUNT);
    let grace = parseInt(import.meta.VITE_SPEEDTEST_GRACE_PING_COUNT);
    pingCount = Number.isNaN(pingCount) ? 10 : pingCount;
    grace = Number.isNaN(grace) ? 2 : grace;
    speedtest.pingTest(speedtestURL, pingCount, grace).then((results) => {
      emitter.emit("onCloudOperatorLatencyUpdated", results.networkPing.value);
    });
  }

  async function startDownloadTest() {
    let duration = parseInt(import.meta.VITE_SPEEDTEST_DL_DURATION);
    let thread = parseInt(import.meta.VITE_SPEEDTEST_DL_THREAD);
    duration = Number.isNaN(duration) ? 5 : duration;
    thread = Number.isNaN(thread) ? 5 : thread;
    if (speedtestURL == null) {
      emitter.emit("onCloudOperatorBandwidthUpdated", -1);
    } else {
      speedtest
        .bandwidthTest(speedtestURL, duration, thread)
        .then((results) => {
          emitter.emit(
            "onCloudOperatorBandwidthUpdated",
            results.networkBandwidth.value
          );
        });
    }
  }

  function sendSpeedTestResults(ping, bandwidth) {
    sendMessage("/candidates/speed-test-results", {
      ping,
      bandwidth,
    });
  }

  function pushHardwareSpecs(hardwareSpecs) {
    sendMessage(
      "/candidates/push-hardware-specs",
      Object.entries(hardwareSpecs).reduce((acc, [key, spec]) => {
        acc[key] = spec.value;
        return acc;
      }, {})
    );
  }

  function pushSurvey(surveyAnswers) {
    sendMessage("/candidates/push-candidate-survey", {
      answers: surveyAnswers,
    });
  }

  function isV2() {
    return yuzu_version === 2;
  }

  return {
    init,
    canStart,
    startSession,
    connectToCloudOperator,
    sendSpeedTestResults,
    disconnectFromCloudOperator,
    startCloudSession,
    pixelStreamingURL,
    speedtestURL,
    startPingTest,
    startDownloadTest,
    emitter,
    pushHardwareSpecs,
    pushSurvey,
    isV2,
  };
});
